/**
 * Copyright (c) 2022 Strategio Digital s.r.o.
 * @author Jiří Zapletal (https://strategio.digital, jz@strategio.digital)
 */
import { TInvoiceHydrated } from '@/types/TInvoice'
import { TInvoiceItemHydrated } from '@/types/TInvoiceItem'
import { useSf } from '@/composables/useSf'

const { callApi } = useSf()

type DownloadResp = { pdf_url: string }
const downloadPdf = async (invoice: TInvoiceHydrated, items: TInvoiceItemHydrated[]): Promise<DownloadResp> => {
    const invoiceNumber = invoice.proforma ? `${invoice.expand.company.proformaPrefix}${invoice.invoiceNumberPretty}` : invoice.invoiceNumberPretty
    const resp = await callApi('/api/invoice/download', {
        method: 'POST',
        body: JSON.stringify({
            invoice: {
                type: invoice.proforma ? 'proforma': 'invoice', // + proforma-annul, invoice-annul
                number: invoiceNumber,
                issue_date: invoice.issueDate,
                due_date: invoice.dueDate,
                currency: invoice.currency,
                items_text: invoice.textBeforeItems,
                footer_text: invoice.expand.company.textOnInvoiceBottom
            },
            payment: {
                method: invoice.paymentType,
                variable_symbol: invoice.variableSymbol,
                bank_account: invoice.expand.company.expand.contact?.bankAccount
            },
            supplier: {
                name: invoice.expand.company.expand.contact?.name,
                street: invoice.expand.company.expand.contact?.street,
                zip: invoice.expand.company.expand.contact?.zip,
                city: invoice.expand.company.expand.contact?.city,
                country: invoice.expand.company.expand.contact?.country,
                company_id: invoice.expand.company.expand.contact?.companyId,
                vat_id: invoice.expand.company.expand.contact?.vatId,
                vat_payer: invoice.expand.company.expand.contact?.vatPayer
            },
            customer: {
                name: invoice.expand.customer.name,
                street: invoice.expand.customer.street,
                zip: invoice.expand.customer.zip,
                city: invoice.expand.customer.city,
                country: invoice.expand.customer.country,
                company_id: invoice.expand.customer.companyId,
                vat_id: invoice.expand.customer.vatId,
                vat_payer: invoice.expand.customer.vatPayer
            },
            items: items.map(item => {
                return {
                    quantity: item.quantity,
                    measure_unit: item.measureUnit,
                    description: item.description,
                    price: item.price,
                }
            })
        })
    })

    return await resp.json();
}

export const invoiceManager = () => {
    return {
        downloadPdf
    }
}